import { CampaignCookieOptions } from "../types/global.types";

const DEFAULT_COOKIE_OPTIONS: CampaignCookieOptions = {
  allowCookies: false,
  allowSessionCookies: false,
  allowPersistentCookies: false,
  expiryDays: "1000",
};

export const getStoredEmail = (
  cookieOptions?: CampaignCookieOptions,
): string => {
  return (
    getStorageItem(
      "brswEmail",
      cookieOptions || DEFAULT_COOKIE_OPTIONS,
    )?.trim() || ""
  );
};

export const resolveInitialEmail = (
  preEmail?: string,
  cookieOptions?: CampaignCookieOptions,
): string | undefined => {
  const fromPre = preEmail?.trim();
  if (fromPre) {
    return fromPre;
  }
  const stored = getStoredEmail(cookieOptions);
  return stored || undefined;
};

// Helper function to get cookie value by name
const getCookie = (name: string): string | null => {
  const value = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")[1];
  return value !== undefined && value !== null ? value : null;
};

// Helper function to set cookie value
const setCookie = (name: string, value: string): void => {
  // Get domain from current hostname
  const hostname = window.location.hostname;
  const domain = hostname.includes(".")
    ? `.${hostname.split(".").slice(-2).join(".")}`
    : hostname;
  document.cookie = `${name}=${value}; path=/; domain=${domain}`;
};

// Helper function to get item from sessionStorage, localStorage, or cookies (in that order)
export const getStorageItem = (
  key: string,
  cookieOptions: CampaignCookieOptions,
): string | null => {
  // First check sessionStorage
  let value = sessionStorage.getItem(key);

  // Then check localStorage
  if (cookieOptions?.allowPersistentCookies) {
    value = localStorage.getItem(key);
  }

  // Finally check cookies
  if (cookieOptions?.allowCookies) {
    value = getCookie(key);
  }
  return value;
};

// Helper function to set item in sessionStorage, localStorage, and cookies
export const setStorageItem = (
  key: string,
  value: string,
  cookieOptions: CampaignCookieOptions = {
    allowCookies: false,
    allowSessionCookies: false,
    allowPersistentCookies: false,
    expiryDays: "1000",
  },
): void => {
  const hasItem = getStorageItem(key, cookieOptions);
  if (hasItem) return;
  // Set in sessionStorage
  sessionStorage.setItem(key, value);

  // Set in localStorage
  localStorage.setItem(key, value);

  // Set in cookies
  setCookie(key, value);
};

// Helper function to update item in sessionStorage, localStorage, and cookies
export const updateStorageItem = (
  key: string,
  value: string,
  cookieOptions: CampaignCookieOptions = {
    allowCookies: false,
    allowSessionCookies: false,
    allowPersistentCookies: false,
    expiryDays: "1000",
  },
): void => {
  sessionStorage.setItem(key, value);

  if (cookieOptions?.allowPersistentCookies) {
    localStorage.setItem(key, value);
  }

  if (cookieOptions?.allowCookies) {
    setCookie(key, value);
  }
};
